import { actionCreators as notificationActions } from '@map/state/notifications';
import { actionCreators as profileActions } from '@map/state/profile';
import { actionCreators as userActions } from '@map/state/user';
import { Profile, User, WithCancelToken } from '@map/types';
import useAction from '@shared/hooks/useAction';
import mixpanel from '@shared/lib/mixpanel/mixpanel';
import { parseAjaxError } from '@shared/setupAxios';
import axios, { AxiosRequestConfig } from 'axios';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { createSelector } from 'reselect';
import { buildApiUrl } from './baseApi';

const userSelector = (state: { user: User }) => state.user;

const localeSelector = createSelector(userSelector, (user) => user.locale || 'en');

export const useLocaleCode = () => useSelector(localeSelector);

export const useCurrentUser = () => useSelector(userSelector);

export type CreateUserInput = {
  access_code: string;
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone: string;
  phone_verification_code: string;
};

const createUser =
  (data: Partial<CreateUserInput>, invitationToken?: string) => async (dispatch: Dispatch) => {
    const role = sessionStorage.getItem('role');
    const postPayload = {
      user: data,
      invitation_token: invitationToken,
      role,
    };
    try {
      const res = await axios.post(buildApiUrl('users'), postPayload, { handlerDisabled: true });
      const { user, profile } = res.data;
      dispatch(userActions.userFetched(user));
      mixpanel.identify(user.id);
      dispatch(profileActions.profileFetched(profile));
      return {
        success: true,
        user,
        profile,
      };
    } catch (ex) {
      if (axios.isAxiosError(ex)) {
        const { error } = ex.response?.data;
        if (error === 'email_taken') {
          return {
            success: false,
            error,
          };
        }
        const responseMessages = parseAjaxError(ex);
        responseMessages.forEach((message) =>
          dispatch(notificationActions.showMessage({ type: 'error', ...message })),
        );
      } else {
        console.warn(ex);
      }
    }

    return {
      success: false,
    };
  };

export const useCreateUser = () => useAction(createUser);

const updateUser =
  (data: Partial<User>, silent?: AxiosRequestConfig['handlerDisabled']) => (dispatch: Dispatch) => {
    return axios
      .patch(buildApiUrl('users'), { user: data }, { handlerDisabled: silent })
      .then(({ data }) => {
        dispatch(userActions.userUpdated(data.user));

        return data.user;
      });
  };
export const useUpdateUser = () => useAction(updateUser);

const verifyUserPassword = ({ password }: { password: string }) => {
  return axios.post(buildApiUrl('users/verify_password'), { password }, { handlerDisabled: true });
};

export const useVerifyUserPassword = () => verifyUserPassword; // no dispatch needed yet

const phoneChanged =
  ({ phone, code }: { phone: string; code: string }) =>
  (dispatch: Dispatch) => {
    return axios
      .post(buildApiUrl('users/phone_changed'), { phone, code }, { handlerDisabled: true })
      .then(({ data }) => {
        dispatch(userActions.userUpdated(data.user));

        return data.user;
      });
  };

export const usePhoneChanged = () => useAction(phoneChanged); // no dispatch needed yet

const updateLocale =
  ({ locale, id }: { locale: string; id?: number }) =>
  (dispatch: Dispatch) => {
    // locale may be set before account exists so
    // just persist the change locally
    if (!id) {
      return Promise.resolve({ locale }).then((user: Partial<User>) =>
        dispatch(userActions.userUpdated(user)),
      );
    }
    return axios
      .patch(buildApiUrl('users'), { locale })
      .then(({ data }) => dispatch(userActions.userUpdated(data.user)));
  };

export const useUpdateLocale = () => useAction(updateLocale);

export const useUpdateLocalUser = () => {
  const dispatch = useDispatch();
  return (user: Partial<User>) => dispatch(userActions.userUpdated(user));
};

const fetchUser =
  ({ cancelToken }: WithCancelToken) =>
  async (dispatch: Dispatch) => {
    const res = await axios.get(buildApiUrl('users'), { cancelToken });
    const { user, profile }: { user: User | null; profile: Profile | null } = res.data;

    if (profile) {
      dispatch(profileActions.profileFetched(profile));
    }
    dispatch(userActions.userFetched(user || {}));
    return { user, profile };
  };
export const useFetchUser = () => useAction(fetchUser);
